/**
 * Selectable size chart drawer (Represent-style).
 * Syncs chosen size with the product variant picker immediately.
 */
(() => {
  const DEFAULT_MEASUREMENTS_CM = {
    XXS: { shoulder: 46, sleeve: 58, length: 66, chest: 52 },
    XS: { shoulder: 48, sleeve: 60, length: 68, chest: 54 },
    S: { shoulder: 50, sleeve: 61, length: 70, chest: 56 },
    M: { shoulder: 52, sleeve: 62, length: 72, chest: 58 },
    L: { shoulder: 54, sleeve: 63, length: 74, chest: 60 },
    XL: { shoulder: 56, sleeve: 64, length: 76, chest: 62 },
    XXL: { shoulder: 58, sleeve: 65, length: 78, chest: 64 },
    '2XL': { shoulder: 58, sleeve: 65, length: 78, chest: 64 },
    '3XL': { shoulder: 60, sleeve: 66, length: 80, chest: 66 },
  };

  const cmToIn = (cm) => Math.round((cm / 2.54) * 10) / 10;

  const normalizeSize = (size) => String(size || '').trim().toUpperCase();

  // Dawn keeps the previous variant-selects node for ~500ms during morph (display:none).
  const getLiveVariantSelects = () => {
    const nodes = [...document.querySelectorAll('product-info variant-selects')];
    return nodes.find((node) => node.style.display !== 'none') || nodes[0] || null;
  };

  class XasSizeChart extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.price = this.dataset.price || '';
      this.selectedSize = null;
      this.unit = 'cm';
      this.measurements = this.loadMeasurements();

      this.table = this.querySelector('[data-xas-size-table]');
      this.cta = this.querySelector('[data-xas-size-cta]');
      this.unitButtons = this.querySelectorAll('.xas-size-chart__unit');

      this.fillMeasurements();
      this.bindEvents();
      this.bindHoverHighlight();
      this.syncFromProduct();
      this.updateCta();
    }

    loadMeasurements() {
      const script = this.querySelector('[data-xas-measurements]');
      if (!script?.textContent?.trim()) return { ...DEFAULT_MEASUREMENTS_CM };

      try {
        const parsed = JSON.parse(script.textContent);
        if (parsed && typeof parsed === 'object') {
          const merged = { ...DEFAULT_MEASUREMENTS_CM };
          Object.keys(parsed).forEach((key) => {
            merged[normalizeSize(key)] = parsed[key];
          });
          return merged;
        }
      } catch (error) {
        console.warn('Xas size chart: invalid measurements metafield JSON', error);
      }

      return { ...DEFAULT_MEASUREMENTS_CM };
    }

    getMeasures(size) {
      const key = normalizeSize(size);
      return this.measurements[key] || null;
    }

    isSizeAvailable(size) {
      const btn = this.querySelector(`.xas-size-chart__size-btn[data-size="${CSS.escape(size)}"]`);
      if (!btn) return false;
      return btn.dataset.available !== 'false';
    }

    fillMeasurements() {
      this.querySelectorAll('.xas-size-chart__cell-btn').forEach((btn) => {
        const measures = this.getMeasures(btn.dataset.size);
        const key = btn.dataset.measure;
        const cm = measures?.[key];
        const cmEl = btn.querySelector('[data-cm]');
        const inEl = btn.querySelector('[data-in]');
        if (cm == null) {
          if (cmEl) cmEl.textContent = '—';
          if (inEl) inEl.textContent = '—';
          return;
        }
        if (cmEl) cmEl.textContent = cm;
        if (inEl) inEl.textContent = cmToIn(cm);
      });
    }

    bindEvents() {
      this.unitButtons.forEach((btn) => {
        btn.addEventListener('click', () => this.setUnit(btn.dataset.unit));
      });

      this.querySelectorAll('.xas-size-chart__size-btn, .xas-size-chart__cell-btn').forEach((btn) => {
        btn.addEventListener('click', () => this.selectSize(btn.dataset.size));
      });

      if (this.cta) {
        this.cta.addEventListener('click', () => this.commitSelection());
      }

      document.addEventListener('change', (event) => {
        if (!event.target.closest('variant-selects')) return;
        this.syncFromProduct();
      });

      document.body.addEventListener('modalClosed', () => {
        if (!this.selectedSize || !this.isSizeAvailable(this.selectedSize)) return;
        requestAnimationFrame(() => this.applySizeToProduct());
      });
    }

    bindHoverHighlight() {
      if (!this.table) return;

      const clearHover = () => {
        this.table.querySelectorAll('.is-hover-row, .is-hover-col').forEach((el) => {
          el.classList.remove('is-hover-row', 'is-hover-col');
        });
      };

      const setHover = (cell) => {
        clearHover();
        const row = cell.closest('tr');
        if (!row) return;

        row.classList.add('is-hover-row');

        const cellIndex = [...row.children].indexOf(cell);
        if (cellIndex < 0) return;

        this.table.querySelectorAll('tr').forEach((tr) => {
          const match = tr.children[cellIndex];
          if (match) match.classList.add('is-hover-col');
        });
      };

      this.table.querySelectorAll('th, td').forEach((cell) => {
        cell.addEventListener('mouseenter', () => setHover(cell));
        cell.addEventListener('focusin', () => setHover(cell));
      });

      this.table.addEventListener('mouseleave', clearHover);
      this.table.addEventListener('focusout', (event) => {
        if (!this.table.contains(event.relatedTarget)) clearHover();
      });
    }

    setUnit(unit) {
      this.unit = unit === 'in' ? 'in' : 'cm';
      this.unitButtons.forEach((btn) => {
        const active = btn.dataset.unit === this.unit;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      this.querySelectorAll('[data-cm]').forEach((el) => {
        el.hidden = this.unit !== 'cm';
      });
      this.querySelectorAll('[data-in]').forEach((el) => {
        el.hidden = this.unit !== 'in';
      });
    }

    selectSize(size, { syncProduct = true } = {}) {
      this.selectedSize = size;
      this.querySelectorAll('.xas-size-chart__size-btn, .xas-size-chart__cell-btn').forEach((btn) => {
        const active = btn.dataset.size === size;
        btn.classList.toggle('is-selected', active);
        if (btn.classList.contains('xas-size-chart__size-btn')) {
          btn.setAttribute('aria-pressed', active ? 'true' : 'false');
        }
      });

      this.table?.querySelectorAll('th, td').forEach((cell) => {
        cell.classList.remove('is-selected-col');
      });

      const headerButtons = [...this.querySelectorAll('.xas-size-chart__size-btn')];
      const colIndex = headerButtons.findIndex((btn) => btn.dataset.size === size);
      if (colIndex >= 0) {
        const nth = colIndex + 2;
        this.table?.querySelectorAll(`tr > :nth-child(${nth})`).forEach((cell) => {
          cell.classList.add('is-selected-col');
        });
      }

      this.updateCta();
      if (syncProduct && this.isSizeAvailable(size)) this.applySizeToProduct();
    }

    updateCta() {
      if (!this.cta) return;

      if (window.xasLaunch && window.xasLaunch.canBuy === false) {
        this.cta.disabled = true;
        this.cta.textContent = window.xasLaunch.buttonLabel || 'Pre-order soon';
        return;
      }

      if (!this.selectedSize) {
        this.cta.disabled = true;
        this.cta.textContent = 'Select a size';
        return;
      }

      if (!this.isSizeAvailable(this.selectedSize)) {
        this.cta.disabled = true;
        this.cta.textContent = 'Sold out';
        return;
      }

      this.cta.disabled = false;
      this.cta.textContent =
        (window.xasLaunch && window.xasLaunch.buttonLabel) || 'Add to cart';
    }

    syncFromProduct() {
      const variantSelects = getLiveVariantSelects();
      if (!variantSelects) return;

      let sizeInput = null;

      variantSelects.querySelectorAll('fieldset').forEach((fieldset) => {
        const legend = fieldset.querySelector('legend');
        if (!legend || !/size/i.test(legend.textContent || '')) return;
        const input = fieldset.querySelector('input:checked');
        if (input) sizeInput = input;
      });

      if (!sizeInput) {
        variantSelects.querySelectorAll('select').forEach((select) => {
          const label = select.closest('.product-form__input')?.querySelector('label, .form__label');
          if (!label || !/size/i.test(label.textContent || '')) return;
          if (select.value) sizeInput = select;
        });
      }

      if (sizeInput?.value) {
        this.selectSize(sizeInput.value, { syncProduct: false });
      } else if (!this.selectedSize) {
        this.querySelectorAll('.xas-size-chart__size-btn, .xas-size-chart__cell-btn').forEach((btn) => {
          btn.classList.remove('is-selected');
          if (btn.classList.contains('xas-size-chart__size-btn')) {
            btn.setAttribute('aria-pressed', 'false');
          }
        });
        this.table?.querySelectorAll('.is-selected-col').forEach((cell) => {
          cell.classList.remove('is-selected-col');
        });
        this.updateCta();
      }
    }

    sizesMatch(a, b) {
      return normalizeSize(a) === normalizeSize(b);
    }

    applySizeToProduct() {
      if (!this.selectedSize || !this.isSizeAvailable(this.selectedSize)) return false;

      const variantSelects = getLiveVariantSelects();
      if (!variantSelects) return false;

      let matched = false;
      variantSelects.querySelectorAll('fieldset').forEach((fieldset) => {
        const legend = fieldset.querySelector('legend');
        if (!legend || !/size/i.test(legend.textContent || '')) return;

        const input = Array.from(fieldset.querySelectorAll('input[type="radio"]')).find((el) =>
          this.sizesMatch(el.value, this.selectedSize)
        );
        if (!input || input.classList.contains('disabled')) return;
        matched = true;
        if (!input.checked) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });

      if (!matched) {
        variantSelects.querySelectorAll('select').forEach((select) => {
          const label = select.closest('.product-form__input')?.querySelector('label, .form__label');
          if (!label || !/size/i.test(label.textContent || '')) return;
          const option = Array.from(select.options).find((opt) => this.sizesMatch(opt.value, this.selectedSize));
          if (!option) return;
          matched = true;
          if (!this.sizesMatch(select.value, this.selectedSize)) {
            select.value = option.value;
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }

      return matched;
    }

    commitSelection() {
      if (!this.selectedSize || !this.isSizeAvailable(this.selectedSize)) return;
      if (window.xasLaunch && window.xasLaunch.canBuy === false) {
        const modal = this.closest('modal-dialog');
        if (modal && typeof modal.hide === 'function') modal.hide();
        return;
      }
      this.applySizeToProduct();

      const modal = this.closest('modal-dialog');
      const addToCart = () => {
        const form = document.querySelector('product-info product-form form[data-type="add-to-cart-form"]');
        const submit = form?.querySelector('[name="add"], [type="submit"]');
        if (submit && !submit.disabled) submit.click();
        if (modal && typeof modal.hide === 'function') modal.hide();
      };

      setTimeout(addToCart, 350);
    }
  }

  if (!customElements.get('xas-size-chart')) {
    customElements.define('xas-size-chart', XasSizeChart);
  }
})();
